import React, { useEffect, useRef, useState } from "react";
import { Textbox } from "../../global/components/inputs/textbox/textbox";
import { LoadingSpinner } from "../../global/components/loading-spinner";
import { IEpiProps, IFacet, IFacetsDisplayState, ISearchRequest, ISearchResultsContainer, ISelectedFilters, SearchContext } from "../models/vertex-search-page-models";
import { useVertexSearchContext, useVertexSearchDispatch } from "../reducers/vertex-search-page-provider";
import { SearchContextSelection } from "./search-context-selection";
import { SearchResultsAllAap } from "./search-results-all-aap";
import { SearchResultsShop } from "./search-results-shop";
import { ShopFacets } from "./shop-facets";
import { sendCloudRetailSearchScriptToGTM } from "../../../static/javascript/modules/shop-google-analytics";
import { Helpers } from "../helpers/helpers";


interface IProps {
}

interface IContextProps {
    epiProps: IEpiProps
    googleVertexCommerceVisitorId: string
    currentSearchContext: string
    currentSearchQuery: string
    showHealthyChildrenBranding: boolean
    disableGenerativeAIOverview: boolean
    disableGenerativeAIOverviewAllAAPContext: boolean
    disableGenerativeAIOverviewHealthyChildrenContext: boolean
    generativeAIOverviewForceEnglishLanguage: boolean
    recaptchaV3SiteKey: string
    searchResultsContainer: ISearchResultsContainer
    selectedShopFiltersString: string
}

export const SearchListing = (props: IProps) => {
    const { epiProps, googleVertexCommerceVisitorId, currentSearchContext, currentSearchQuery, showHealthyChildrenBranding, disableGenerativeAIOverview, disableGenerativeAIOverviewAllAAPContext, disableGenerativeAIOverviewHealthyChildrenContext, generativeAIOverviewForceEnglishLanguage, recaptchaV3SiteKey, searchResultsContainer, selectedShopFiltersString }: IContextProps = useVertexSearchContext();
    const dispatch = useVertexSearchDispatch();
    const [searchInput, setSearchInput] = useState(currentSearchQuery);
    const [isLoading, setIsLoading] = useState(false);
    const [isLoadingMoreResults, setIsLoadingMoreResults] = useState(false);
    const [isRecaptchaReady, setIsRecaptchaReady] = useState(false);

    const latestRequestRef = useRef({ timestamp: 0 });

    useEffect(() => {
        // This code runs only once, on the first render, after Recaptcha is ready. If the search query was set in the query parameters in the URL, and its length is at least 2 characters long, get search results.
        if (isRecaptchaReady && currentSearchQuery && currentSearchQuery.length > 1) {
            getSearchResults(currentSearchQuery, currentSearchContext, "", selectedShopFiltersString, null);
        }

    }, [isRecaptchaReady]);

    useEffect(() => {
        // This code runs only once, on the first render, before Recaptcha is ready. If the search query was set in the query parameters in the URL, and its length is at least 2 characters long, show loading spinner. Search results will load after Recaptcha is ready.
        if (!isRecaptchaReady && currentSearchQuery && currentSearchQuery.length > 1) {
            setIsLoading(true);
        }

    }, []);

    useEffect(() => {
        // Load reCAPTCHA v3 script on the first render.
        const script = document.createElement('script');
        script.src = `https://www.google.com/recaptcha/api.js?render=` + recaptchaV3SiteKey;
        script.async = true;

        script.onload = () => {
            if ((window as any).grecaptcha) {
                (window as any).grecaptcha.ready(() => {
                    setIsRecaptchaReady(true);
                });
            }
        };

        document.head.appendChild(script);

        return () => {
            document.head.removeChild(script);
        };
    }, []);

    function onSearchClick() {
        setSelectedShopFiltersInContext({}); //Clear any filters in SHOP context.
        setSelectedShopFiltersStringInContext("");
        Helpers.removeQueryParamFromUrl("facets");
        setFacetsShowMoreStateInContext({});
        setFacetsAccordionClosedStateInContext({});
        getSearchResults(searchInput, currentSearchContext, "", "", null);
    }

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter' && searchInput.length > 1) {
            onSearchClick();
        }
    };

    function getSearchResults(searchQuery: string, searchContext: string, pageToken: string, selectedShopFilters: string, currentShopFacets: IFacet[]) {
        handleLoadingState(true, pageToken);

        // Retrieve Recaptcha v3 Token
        let tokenPromise: Promise<string>;

        if ((window as any).grecaptcha) {
            tokenPromise = (window as any).grecaptcha.execute(recaptchaV3SiteKey, {
                action: 'search'
            }).catch((error: any) => {
                console.error('reCAPTCHA error:', error);
                return "";
            });
        } else {
            tokenPromise = Promise.resolve("");
        }

        // Create timestamp for this request
        const requestTime = Date.now();
        // Update the ref immediately (this happens synchronously)
        latestRequestRef.current = { timestamp: requestTime };

        // Handle the token when it's ready
        let disableGenerativeAIOverviewInRequest = getDisableGenerativeAiOverview(searchContext);

        tokenPromise.then(token => {
            let request: ISearchRequest = {
                searchQuery: searchQuery,
                currentSearchContext: searchContext,
                pageToken: pageToken,
                shopFilters: selectedShopFilters,
                currentShopFacets: currentShopFacets,
                disableGenerativeAIOverview: disableGenerativeAIOverviewInRequest,
                generativeAIOverviewForceEnglishLanguage: generativeAIOverviewForceEnglishLanguage,
                recaptchaV3Token: token
            }
            setCurrentSearchQuery(searchQuery);
            setCurrentSearchQueryToUrl(searchQuery);

            sendSearchRequest(request).then(response => {
                // Only show search results if the results are for the latest request.
                //This is because sometimes there can be a mismatch if users search and switch contexts before results are loaded.
                if (response != null &&
                    latestRequestRef.current.timestamp === requestTime) {
                    if (pageToken == null || pageToken == "") {
                        //If pageToken is an empty string and 1st page of results is being returned
                        setSearchResults(response);
                        handleLoadingState(false, pageToken);
                        handleCloudRetailSearchScript(response, searchContext, selectedShopFilters, searchQuery);
                    }
                    else {
                        //If we are loading past the first page of results, append new page of results to existing results list.
                        var currentSearchResultsContainer = searchResultsContainer;
                        response.searchResults = currentSearchResultsContainer.searchResults.concat(response.searchResults);
                        setSearchResults(response);
                        handleLoadingState(false, pageToken);
                        handleCloudRetailSearchScript(response, searchContext, selectedShopFilters, searchQuery);
                    }
                }
                else {
                    handleLoadingState(false, pageToken);
                }
            })
        });
    }

    function getDisableGenerativeAiOverview(searchContext: string) {
        let disableGenerativeAIOverviewInRequest = false;
        if (disableGenerativeAIOverview == true) disableGenerativeAIOverviewInRequest = true;
        if (disableGenerativeAIOverviewAllAAPContext == true && searchContext == SearchContext.AllAAP) disableGenerativeAIOverviewInRequest = true;
        if (disableGenerativeAIOverviewHealthyChildrenContext === true && searchContext == SearchContext.HealthyChildren) disableGenerativeAIOverviewInRequest = true;
        return disableGenerativeAIOverviewInRequest;
    }

    function handleLoadingState(newLoadingState: boolean, pageToken: string) {
        //If no page token is present, the first page of results is being loaded. Set the general loading state.
        if (pageToken == null || pageToken == "") {
            setIsLoading(newLoadingState);
        }
        //If a page token is present, additional results are being loaded (when the user clicks 'load more'). Set the load more CTA loading state.
        if (pageToken && pageToken.length > 0) {
            setIsLoadingMoreResults(newLoadingState);
        }
    }

    function onLoadMoreClick() {
        //Get the next set of search results using the nextPageToken in the last search response.
        //For the SHOP context, include the facets from the lat search response and include any selected filters.
        getSearchResults(currentSearchQuery, currentSearchContext, searchResultsContainer.nextPageToken, selectedShopFiltersString, searchResultsContainer.facets);
    }

    function onRelatedQuestionClick(relatedQuestion: string) {
        setSearchInput(relatedQuestion);
        getSearchResults(relatedQuestion, currentSearchContext, "", "", null);
    }

    function onShopFiltersChange(shopFiltersString: string) {
        //Send current facets to search method, and these same ones will be returned (instead of pulling new facets from Google Vertex API).
        getSearchResults(currentSearchQuery, currentSearchContext, "", shopFiltersString, searchResultsContainer.facets);
    }

    async function sendSearchRequest(request: ISearchRequest) {
        const url = '/api/v1/vertexSearch/search';
        const options = {
            method: 'POST',
            headers: {
                "Content-Type": "application/json"
            },
            body: JSON.stringify(request),
        };
        const response = await fetch(url, options);
        return response.json();
    }

    function setSearchResults(searchResultsContainer: ISearchResultsContainer) {
        dispatch({
            type: 'updateSearchResults',
            data: searchResultsContainer
        })
    }

    function handleCloudRetailSearchScript(searchResultsContainer: ISearchResultsContainer, searchContext: string, selectedShopFilters: string, searchQuery: string) {
        if (searchContext == SearchContext.ShopAAP) { //Used by Google Vertex for Commerce only.
            let shouldSendCloudRetailScriptToGTMString = (window as any).bootstrap.shouldSendCloudRetailScriptToGTM;
            let shouldSendCloudRetailScriptToGTM = shouldSendCloudRetailScriptToGTMString != null && shouldSendCloudRetailScriptToGTMString.length > 0 && shouldSendCloudRetailScriptToGTMString.toLowerCase() == 'true';
            if (searchResultsContainer != null
                && searchResultsContainer.searchResults != null
                && searchResultsContainer.searchResults.length > 0
                && shouldSendCloudRetailScriptToGTM == true
            ) {
                let productIds = searchResultsContainer.searchResults.map(searchResult => searchResult.id);
                sendCloudRetailSearchScriptToGTM(productIds, googleVertexCommerceVisitorId, searchResultsContainer.attributionToken, selectedShopFilters, searchQuery);
            }
        }

    }

    function setCurrentSearchQuery(currentSearchQuery: string) {
        dispatch({
            type: 'updateCurrentSearchQuery',
            data: currentSearchQuery
        })
    }

    function setCurrentSearchQueryToUrl(searchQuery: string) {
        Helpers.setQueryParamToUrl('k', searchQuery);
    }

    function setCurrentContextQueryToUrl(searchContext: string) {
        Helpers.setQueryParamToUrl('context', searchContext);
    }

    function setSelectedShopFiltersInContext(selectedShopFilters: ISelectedFilters) {
        dispatch({
            type: 'updateSelectedShopFilters',
            data: selectedShopFilters
        })
    }

    function setSelectedShopFiltersStringInContext(selectedShopFiltersString: string) {
        dispatch({
            type: 'updateSelectedShopFiltersString',
            data: selectedShopFiltersString
        })
    }

    function setFacetsShowMoreStateInContext(facetsShowMoreState: IFacetsDisplayState) {
        dispatch({
            type: 'updateFacetsShowMoreState',
            data: facetsShowMoreState
        })
    }

    function setFacetsAccordionClosedStateInContext(facetsAccordionClosedState: IFacetsDisplayState) {
        dispatch({
            type: 'updateFacetsAccordionClosedState',
            data: facetsAccordionClosedState
        })
    }

    function onSearchContextClick(searchContext: string) {
        setCurrentContextQueryToUrl(searchContext);
        dispatch({
            type: 'updateCurrentSearchContext',
            data: searchContext
        });

        setSearchInput(currentSearchQuery);
        setSelectedShopFiltersInContext({}); //Clear any filters in SHOP context.
        setSelectedShopFiltersStringInContext("");
        Helpers.removeQueryParamFromUrl("facets");
        setFacetsShowMoreStateInContext({});
        setFacetsAccordionClosedStateInContext({});

        if (currentSearchQuery && currentSearchQuery.length > 1) {
            getSearchResults(currentSearchQuery, searchContext, "", "", null);
        }
    }

    let useHealthyChildrenBrandingForSearchInput = showHealthyChildrenBranding && currentSearchContext == SearchContext.HealthyChildren;
    let searchPlaceholderText = "";
    if (useHealthyChildrenBrandingForSearchInput) {
        searchPlaceholderText = epiProps.healthyChildrenSearchPlaceholderText;
    }
    else if (currentSearchContext == SearchContext.HealthyChildren || currentSearchContext == SearchContext.AllAAP) {
        searchPlaceholderText = epiProps.searchPlaceholderText;
    }
    else {
        //SHOP context
        searchPlaceholderText = epiProps.shopSearchPlaceholderText;
    }

    return (
        <div className={`${useHealthyChildrenBrandingForSearchInput ? 'hc-theme' : 'default-theme'}`}>
            <div className="search-bar-area vertex-search-bar">
                <div className="search-bar-area-input">
                    <svg className="magnifying-glass">
                        <use xlinkHref="#info-magnifying-glass"></use>
                    </svg>
                    <Textbox
                        id={"vertex-search-input-id"}
                        labelText={""}
                        defaultValue={""}
                        onInput={e => setSearchInput(e.currentTarget.value)}
                        placeHolder={searchPlaceholderText}
                        testID="vertex-search-input"
                        required={false}
                        showOptionalRequired={false}
                        isControlled={true}
                        value={searchInput}
                        hasError={false}
                        disabled={false}
                        type={"text"}
                        onKeyDown={handleKeyDown}
                    />
                </div>
                <button className="search-bar-area-btn vertex-search-bar-btn"
                    disabled={searchInput.length < 2}
                    onClick={() => onSearchClick()}>{epiProps.searchCtaText}</button>
            </div>
            {
                currentSearchContext == SearchContext.ShopAAP &&
                <div className="vertex-search-facets-mobile">
                    <ShopFacets
                        onShopFiltersChange={onShopFiltersChange}
                    />
                </div>
            }
            <SearchContextSelection
                onSearchContextClick={onSearchContextClick}
            />
            <div className={`${currentSearchContext == SearchContext.ShopAAP || isLoading ? "vertex-search-shop" : ""}`}>
                {
                    currentSearchContext == SearchContext.ShopAAP &&
                    <div className="vertex-search-facets-desktop">
                        <ShopFacets
                            onShopFiltersChange={onShopFiltersChange}
                        />
                    </div>
                }
            
                {
                    isLoading == true ? <div className="vertex-search-result-loading"><LoadingSpinner
                        loading
                    /></div> : null
                }

                {
                    (currentSearchContext == SearchContext.HealthyChildren || currentSearchContext == SearchContext.AllAAP)
                    && !isLoading &&
                    <SearchResultsAllAap
                        isLoading={isLoading}
                        isLoadingMoreResults={isLoadingMoreResults}
                        onLoadMore={onLoadMoreClick}
                        onRelatedQuestionClick={onRelatedQuestionClick}
                    />
                }

                {!isLoading && <SearchResultsShop
                    isLoading={isLoading}
                    isLoadingMoreResults={isLoadingMoreResults}
                    onLoadMore={onLoadMoreClick}
                    onShopFiltersChange={onShopFiltersChange}
                />}
            </div>
        </div>
    )
}
